import React, { FC, useEffect, useState } from 'react'
import { CrewTraining } from '../core/_requests'

type Props = {
  visible: boolean
  training: CrewTraining | null
  isLoading?: boolean
  onClose: () => void
  onSaveRemarks: (officeRemarks: string) => void
  onReview: (officeRemarks: string) => void
  onReject: (payload: { officeRemarks: string; reason: string }) => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const CrewTrainingReviewModal: FC<Props> = ({
  visible,
  training,
  isLoading = false,
  onClose,
  onSaveRemarks,
  onReview,
  onReject,
}) => {
  const [officeRemarks, setOfficeRemarks] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!visible) return
    setOfficeRemarks(training?.officeRemarks || '')
    setRejectionReason(training?.rejectionReason || '')
    setError('')
  }, [visible, training])

  if (!visible || !training) return null

  const validateOfficeRemarks = () => {
    if (!officeRemarks.trim()) {
      setError('Office remarks are required')
      return false
    }
    setError('')
    return true
  }

  const validateRejection = () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required')
      return false
    }
    setError('')
    return true
  }

  return (
    <div style={WRAP_STYLE}>
      <div className='bg-white rounded shadow-lg' style={{ width: '60vw', maxWidth: 900 }}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='m-0'>Review Crew Training</h5>
          <button type='button' className='btn-close' onClick={onClose} disabled={isLoading} />
        </div>

        <div className='p-5'>
          <div className='mb-4 text-muted'>
            {training.crewName} | {training.rankName} | {training.trainingType}
          </div>
          <label className='form-label fw-semibold'>Office Remarks <span className='text-danger'>*</span></label>
          <textarea
            className='form-control mb-4'
            rows={4}
            value={officeRemarks}
            onChange={(e) => {
              setOfficeRemarks(e.target.value)
              if (error) setError('')
            }}
            placeholder='Enter office remarks'
            disabled={isLoading}
          />

          <label className='form-label fw-semibold'>Rejection Reason <span className='text-muted'>(Required for reject)</span></label>
          <textarea
            className='form-control'
            rows={3}
            value={rejectionReason}
            onChange={(e) => {
              setRejectionReason(e.target.value)
              if (error) setError('')
            }}
            placeholder='Enter rejection reason'
            disabled={isLoading}
          />

          {error && <div className='text-danger small mt-2'>{error}</div>}
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4'>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-light-primary'
            disabled={isLoading}
            onClick={() => {
              if (!validateOfficeRemarks()) return
              onSaveRemarks(officeRemarks)
            }}
          >
            Save Review
          </button>
          <button
            type='button'
            className='btn btn-warning'
            disabled={isLoading}
            onClick={() => {
              if (!validateOfficeRemarks()) return
              onReview(officeRemarks)
            }}
          >
            Mark Reviewed
          </button>
          <button
            type='button'
            className='btn btn-danger'
            disabled={isLoading}
            onClick={() => {
              if (!validateRejection()) return
              onReject({ officeRemarks, reason: rejectionReason })
            }}
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewTrainingReviewModal

